import type { ReactRouter } from './ReactRouter';
import type { ReactRouteSnapshot } from './types';
import { useSignals } from '@preact/signals-react/runtime';
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { SpinnerOverlay } from 'src/components/spinner/spinner';
import type { AppLocation } from 'src/lib/navigation/types';
import type { RouteParams } from 'src/lib/router/core';
import type { NavigationService } from 'src/services/NavigationService';

interface RouterContextValue {
    readonly snapshot: ReactRouteSnapshot;
    readonly navigation: NavigationService;
}

interface RouteLevelContextValue {
    readonly index: number;
}

const RouterContext = createContext<RouterContextValue | null>(null);
const RouteLevelContext = createContext<RouteLevelContextValue | null>(null);
const DEFAULT_FALLBACK = <SpinnerOverlay />;
const EMPTY_PARAMS: RouteParams = {};

const RouteLevel = ({ index }: { readonly index: number }): ReactNode => {
    const { snapshot } = useRouterContext();
    const nextIndex = snapshot.matches.findIndex((match, matchIndex) => matchIndex >= index && match.loaded);
    if (nextIndex < 0) return null;

    const Component = snapshot.matches[nextIndex]!.loaded;
    if (!Component) return null;

    return (
        <RouteLevelContext.Provider value={{ index: nextIndex }}>
            <Component />
        </RouteLevelContext.Provider>
    );
};

export const RouterProvider = ({
    children,
    fallback = DEFAULT_FALLBACK,
    router,
}: {
    readonly children?: ReactNode;
    readonly fallback?: ReactNode;
    readonly router: ReactRouter;
}) => {
    useSignals();
    const snapshot = router.current.value;
    if (!snapshot) return fallback;

    return (
        <RouterContext.Provider value={{ navigation: router.navigation, snapshot }}>
            <RouteLevel index={0} />
            {children}
        </RouterContext.Provider>
    );
};

export const Outlet = () => {
    const level = useContext(RouteLevelContext);
    if (!level) {
        throw new Error('Outlet must render within a matched route component');
    }
    return <RouteLevel index={level.index + 1} />;
};

export const useRouterContext = (): RouterContextValue => {
    const value = useOptionalRouterContext();
    if (!value) {
        throw new Error('Router hooks must be used within RouterProvider');
    }
    return value;
};

export const useRouteLevelContext = (): RouteLevelContextValue => {
    const value = useOptionalRouteLevelContext();
    if (!value) {
        throw new Error('Route-level hooks must be used within a matched route component');
    }
    return value;
};

export const useOptionalRouterContext = (): RouterContextValue | null => useContext(RouterContext);

export const useOptionalRouteLevelContext = (): RouteLevelContextValue | null => useContext(RouteLevelContext);

export const useRouterNavigation = (): NavigationService | null => useOptionalRouterContext()?.navigation ?? null;

/** @internal Owned-router context for test hosts. */
export const RouterTestProvider = ({
    children,
    location,
    navigation,
    params = EMPTY_PARAMS,
    pathnameBase = location.pathname,
}: {
    readonly children: ReactNode;
    readonly location: AppLocation;
    readonly navigation: NavigationService;
    readonly params?: RouteParams;
    readonly pathnameBase?: string;
}) => {
    const snapshot: ReactRouteSnapshot = {
        location,
        matches: [
            {
                definition: { id: 'test-route' },
                loaded: null,
                params,
                pathnameBase,
            },
        ],
        params,
    };
    return (
        <RouterContext.Provider value={{ navigation, snapshot }}>
            <RouteLevelContext.Provider value={{ index: 0 }}>{children}</RouteLevelContext.Provider>
        </RouterContext.Provider>
    );
};
